import type { DeviceInfo } from '@capacitor/device';
import { Device } from '@capacitor/device';
import { computed, ref } from 'vue';
import { useCache } from '@/composables/cache';
import { getCurrentTimestamp, getDateDiffMinutes } from '@/utils/dateUtil';
import { useAppStore } from '@/stores/app';
import { useQuasar } from 'quasar';
export const useDevice = () => {
  const deviceId = ref();
  const { latestSyncActiveStatus } = useCache();
  const appStore = useAppStore();
  const { screen } = useQuasar();

  const isSmallScreen = computed(() => {
    return screen.xs;
  });

  const isTablet = computed(() => {
    return screen.name === 'sm';
  });

  const isDesktop = computed(() => {
    return screen.md || screen.lg;
  });

  const isMiniDesktop = computed(() => {
    return screen.width <= 1260;
  });

  const isDesktopLessWidth1200 = computed(() => {
    return screen.width <= 1200;
  });

  const isDesktopLessWidth1400 = computed(() => {
    return screen.width <= 1400;
  });

  const isTabletLessWidth870 = computed(() => {
    return screen.width < 870;
  });

  const isTabletLessWidth800 = computed(() => {
    return screen.width < 800;
  });

  const isTabletLessWidth680 = computed(() => {
    return screen.width < 680;
  });

  const isMobile = computed(() => {
    return screen.xs;
  });

  const isTabletLessWidth850 = computed(() => {
    return screen.width < 850;
  });

  const isNewTablet = computed(() => {
    return screen.sm || screen.md
  });

  const isNewDesktop = computed(() => {
    return screen.gt.sm
  });

  const isTablet820 = computed(() => {
    return screen.width <= 840;
  });

  const getDeviceInfo = async (): Promise<DeviceInfo> => {
    const info = await Device.getInfo();
    return new Promise((resolve) => {
      resolve(info);
    });
  };
  const isMobileOrTablet = async (): Promise<boolean> => {
    const info = await getDeviceInfo();
    return new Promise((resolve) => {
      resolve(
        info.operatingSystem == 'android' || info.operatingSystem == 'ios',
      );
    });
  };

  const getDeviceId = async (): Promise<string | undefined> => {
    const ID = await Device.getId();
    return new Promise((resolve) => {
      deviceId.value = ID ? ID.identifier : undefined;
      resolve(ID ? ID.identifier : undefined);
    });
  };
  const isWeb = async (): Promise<boolean> => {
    const info = await Device.getInfo();
    return new Promise((resolve) => {
      resolve(info.platform == 'web');
    });
  };
  const canSyncActiveStatusToServer = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const currentTimeTamp = getCurrentTimestamp();
      let diffminutes;
      if (latestSyncActiveStatus.value > 0) {
        diffminutes = getDateDiffMinutes(
          latestSyncActiveStatus.value,
          currentTimeTamp,
        );
      } else {
        latestSyncActiveStatus.value = currentTimeTamp;
        resolve(true);
      }
      if (diffminutes != undefined && diffminutes >= 5) {
        latestSyncActiveStatus.value = currentTimeTamp;
        resolve(true);
      }
      resolve(false);
    });
  };
  const setSysncActiveStatus = () => {
    latestSyncActiveStatus.value = getCurrentTimestamp();
  };

  const initialDevice = () => {
    isMobileOrTablet().then((result) => {
      appStore.setMobileOrTablet(result);
    });
  };

  return {
    getDeviceInfo,
    isWeb,
    getDeviceId,
    deviceId,
    canSyncActiveStatusToServer,
    setSysncActiveStatus,
    isMobileOrTablet,
    initialDevice,
    isSmallScreen,
    isTablet,
    isDesktop,
    isMiniDesktop,
    isDesktopLessWidth1200,
    isDesktopLessWidth1400,
    isTabletLessWidth870,
    isMobile,
    isNewTablet,
    isNewDesktop,
    isTabletLessWidth850,
    isTabletLessWidth680,
    isTabletLessWidth800,
    isTablet820
  };
};
